import { useState } from 'react';
import { Link } from 'react-router';
import { Avatar, Wordmark } from '@/components/brand/Brand';
import { FeedCard } from '@/components/social/FeedCard';
import { Button, ButtonLink } from '@/components/ui/Button';
import { EmptyState, Skeleton } from '@/components/ui/Feedback';
import { courseNameFrom, useCourseIndex, useFeed, useFriendRequests } from '@/lib/api/queries';
import { useCurrentUser } from '@/lib/auth/AuthContext';
import { routes } from '@/routes/paths';
import { TimelineSidebar } from './TimelineSidebar';
import styles from './Timeline.module.css';
import { t } from '@/lib/i18n';

const PAGE_SIZE = 10;

export function Timeline() {
  const user = useCurrentUser();
  const [limit, setLimit] = useState(PAGE_SIZE);
  const feed = useFeed(limit);
  const courseIndex = useCourseIndex();
  const requests = useFriendRequests();

  const items = feed.data?.items ?? [];
  const total = feed.data?.total ?? 0;
  const pendingCount = requests.data?.received.length ?? 0;

  return (
    <div className={styles.page}>
      <div className={styles.hero}>
        <div className={styles.heroBar}>
          <span className={styles.heroWordmark}>
            <Wordmark height={22} />
          </span>
          <span className={styles.heroActions}>
            <Link to={routes.friends} className={styles.friendsLink}>
              {t('friends.label')}
              {pendingCount > 0 && <span className={styles.badge}>{pendingCount}</span>}
            </Link>
            <Link to={routes.profile} className={styles.heroAvatar} aria-label={t('nav.aria.profile')}>
              <Avatar name={user.displayName} size={34} />
            </Link>
          </span>
        </div>
      </div>

      {/*
        Duas colunas no desktop: a de leitura, de largura fixa, e a lateral. No celular a
        lateral não existe e o grid vira uma coluna só, então o mesmo markup serve os dois.
      */}
      <div className={styles.layout}>
        {feed.isLoading ? (
          <FeedSkeleton />
        ) : items.length === 0 ? (
          <div className={styles.empty}>
            <EmptyState
              title={t('timeline.empty.title')}
              actions={
                <>
                  <ButtonLink to={routes.addFriend}>{t('timeline.addFriends')}</ButtonLink>
                  <ButtonLink to={routes.record} variant="secondary">
                    {t('timeline.recordRound')}
                  </ButtonLink>
                </>
              }
            >
              {t('timeline.empty.body')}
            </EmptyState>
          </div>
        ) : (
          <div className={styles.feed}>
            {items.map((item) => (
              <FeedCard
                key={item.id}
                item={item}
                courseName={courseNameFrom(courseIndex.data, item.courseId)}
                viewerId={user.id}
              />
            ))}

            {total > items.length && (
              <div className={styles.more}>
                <Button
                  variant="secondary"
                  block
                  busy={feed.isFetching}
                  onClick={() => setLimit((n) => n + PAGE_SIZE)}
                >
                  {t('timeline.loadMore', { count: Math.min(PAGE_SIZE, total - items.length) })}
                </Button>
                <p className={styles.count}>
                  {t('timeline.showing', { shown: items.length, total })}
                </p>
              </div>
            )}
          </div>
        )}

        <TimelineSidebar />
      </div>
    </div>
  );
}

function FeedSkeleton() {
  return (
    <div className={styles.feed}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--border-soft)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-4)',
          }}
        >
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <Skeleton width={38} height={38} radius="999px" />
            <div style={{ flex: 1 }}>
              <Skeleton width="45%" height={14} />
              <Skeleton width="65%" height={10} style={{ marginTop: 6 }} />
            </div>
          </div>
          <Skeleton height={54} style={{ marginTop: 16 }} />
        </div>
      ))}
    </div>
  );
}
